import React, { useEffect, useState } from "react";
import { Box, Grid, Card, Typography, Stack, Divider } from "@mui/material";
import BatteryChargingFullIcon from "@mui/icons-material/BatteryChargingFull";
import AccountBalanceWalletIcon from "@mui/icons-material/AccountBalanceWallet";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import SwapHorizontalCircleIcon from "@mui/icons-material/SwapHorizontalCircle";
import SummarizeIcon from "@mui/icons-material/Summarize";
import { endpoints } from "../../../utils/endpoints";
import DotLoader from "../../../components/dotLoader";
import SmartphoneIcon from "@mui/icons-material/Smartphone";

import moment from "moment";

const MetricCard = ({ label, value, color, isLoading }) => (
  <Box sx={{ p: 1, textAlign: "center" }}>
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    {isLoading || value === null ? (
      <DotLoader sx={{ mt: 1 }} />
    ) : (
      <Typography variant="h6" sx={{ fontWeight: "bold", color }}>
        {`₹${value}`}
      </Typography>
    )}
  </Box>
);

const InfoCard = ({ icon, label, value, subText, color }) => (
  <Card variant="outlined" sx={{ textAlign: "center", p: 2 }}>
    {icon && (
      <Box mb={1} display="flex" justifyContent="center">
        {React.cloneElement(icon, { fontSize: "large", sx: { color } })}
      </Box>
    )}
    <Typography variant="body2" color="text.secondary">
      {label}
    </Typography>
    <Typography variant="h6" sx={{ fontWeight: "bold", color }}>
      {value}
    </Typography>
    {subText && (
      <Typography variant="caption" color="text.secondary">
        {subText}
      </Typography>
    )}
  </Card>
);

// Wrapper for sections
const SwapInfoWrapper = ({ title, icon, bgColor, children, sx }) => (
  <Box sx={{ bgcolor: bgColor, p: 2, ...sx }}>
    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
      {icon}
      <Typography variant="subtitle1" fontWeight="bold">
        {title}
      </Typography>
    </Stack>
    {children}
  </Box>
);

export default function SwapInfoSection({ data, transactionId }) {
  const [transactionData, setSwapTransactionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  
  useEffect(() => {
    console.log("finalUpdateDone: ", data?.finalUpdateDone);
    console.log('transactionId: ', transactionId);
    if (data?.finalUpdateDone) {
      return setSwapTransactionData(data);
    }
    let isMounted = true;
    let timer;
    console.log("fetchData");
    const fetchData = async () => {
      if (transactionId) {
        setIsLoading(true);
        await getSwapTransaction();
        if (isMounted) setIsLoading(false);
      } else {
        console.log("transactionId undefined! ", transactionId);
      }
    };

    timer = setTimeout(fetchData, 5000);

    return () => {
      isMounted = false;
      clearTimeout(timer);
    };
  }, [data]);

  const batteryDetails = [
    {
      label: "Taken Battery SOC",
      value: `${data.takenBatterySoc}%`,
      subText: `ID: ${data.takenBatteryId}`,
      icon: <BatteryChargingFullIcon />,
      color: "green",
    },
    {
      label: "Inserted Battery SOC",
      value: `${data.insertedBatterySoc}%`,
      subText: `ID: ${data.insertedBatteryId}`,
      icon: <BatteryChargingFullIcon />,
      color: "red",
    },
    {
      label: "Battery From Door",
      value: data.batteryTakenFromDoor,
    },
    {
      label: "Battery To Door",
      value: data.batteryInsertedIntoDoor,
    },
  ];

  const metricsConfig = [
    { label: "Swap Fee", key: "swapFee", color: "blue" },
    { label: "Daily Rental Fee", key: "penaltyFee", color: "orange" },
    { label: "Total Rental", key: "totalPenaltyApplicable", color: "red" },
    { label: "Total Swap Fee", key: "totalSwapFee", color: "purple" },
    { label: "Collection Amount", key: "collectionAmount", color: "green" },
    { label: "Past Rentals", key: "totalPastRentals", color: "gray" },
  ];

  const getSwapTransaction = async () => {
    try {
      console.log(transactionId, "getSwapTransaction call...");
      setIsLoading(true);
      const url = `${endpoints.manualSwap.getSwapLogsBaas}?transactionId=${transactionId}`;
      const res = await fetch(url);
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      const data = await res.json();
      const swapData = data?.data?.length > 0 ? data?.data[0] : {};
      console.log("swapData $$$: ", swapData);
      setSwapTransactionData(swapData);
      setIsLoading(false);
    } catch (error) {
      console.error("Error:", error);
    }
  };

  const financialMetrics = metricsConfig.map((metric) => ({
    ...metric,
    value: transactionData?.[metric.key] ?? null,
  }));

  const transformDate = (date) => {
    let d = new Date(date);
    return moment(d).format("DD-MM-YYYY, hh:mm:ss");
  };

  return (
    <Box mt={3}>
      {/* Battery Swap Section */}
      <Box
        sx={{
          background:
            "linear-gradient(135deg, rgba(45,212,136,0.8) 0%,  rgba(13,148,136,0.8) 100%)",
          color: "white",
          p: 2,
          borderRadius: "8px 8px 0 0",
        }}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Box width="100%">
            <Typography variant="h6" fontWeight="bold" sx={{ mb: 1 }}>
              Battery Swap Transaction
            </Typography>
            <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Transaction ID: </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {data.transactionId}
                </Typography>
              </Stack>

              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2">Date & Time: </Typography>
                <Typography variant="body2" fontWeight={600}>
                  {transformDate(data.createdAt._seconds * 1000)}
                </Typography>
              </Stack>
            </Box>
          </Box>
        </Stack>
      </Box>

      <SwapInfoWrapper
        title="Battery Swap Details"
        icon={<SwapHorizontalCircleIcon color="success" />}
        bgColor="#ECFDF5"
      >
        <Grid container spacing={2}>
          {batteryDetails.map((item, index) => (
            <Grid item xs={6} md={6} key={index}>
              <InfoCard {...item} />
            </Grid>
          ))}
        </Grid>
      </SwapInfoWrapper>

      <Divider sx={{ color: "#EAEFEF" }} />

      {/* Financial Summary Section */}
      <SwapInfoWrapper
        title="Financial Summary"
        icon={<AccountBalanceWalletIcon color="warning" />}
        bgColor="#FFFBEB"
        sx={{ borderRadius: "0 0 8px 8px" }}
      >
        <Grid container spacing={2}>
          {financialMetrics.map((item, index) => (
            <Grid item xs={6} md={4} key={index}>
              <MetricCard {...item} isLoading={isLoading} />
            </Grid>
          ))}
        </Grid>
      </SwapInfoWrapper>
    </Box>
  );
}
